/**
 * Spec 062 — phone-shaped preview of the customer app's offer hero card and offer sheet.
 *
 * Self-contained on purpose: no app imports, its own copy strings and its own CSS, so the
 * admin portal can copy this one file verbatim (the two portals are separate builds).
 * Brand: Alexandria, navy #030c54, yellow #fff200. The overlay text is rendered from data,
 * never baked into the image (AC-10). Prices are a preview; the server is authoritative. `items` must be
 * in the server's order (OfferResponse.items / sortOfferItems): the first one is the card's lead.
 */

export interface OfferPhonePreviewItem {
  offeringId: string;
  name: string;
  listPrice: number;
  discountedPrice: number;
}

export interface OfferPhonePreviewProps {
  lang: 'en' | 'ar';
  theme: 'light' | 'dark';
  garageName: string;
  garageRating?: number | null;
  garageRatingCount?: number | null;
  titleEn: string;
  titleAr?: string | null;
  subtitleEn?: string | null;
  subtitleAr?: string | null;
  discountType: 'PERCENT' | 'FIXED';
  discountValue: number;
  /**
   * Spec 073: set when the services' discounts differ — the card then reads "Up to X% OFF" like the
   * app (X = the server preview's highest percent saving). Omitted/null → the single-value badge.
   */
  badgeUpTo?: { type: 'PERCENT' | 'FIXED'; value: number } | null;
  bannerUrl?: string | null;
  bannerArUrl?: string | null;
  /** ISO yyyy-MM-dd */
  startsAt?: string | null;
  /** ISO yyyy-MM-dd */
  endsAt?: string | null;
  items: OfferPhonePreviewItem[];
  maxBookings?: number | null;
  bookingsUsed?: number | null;
  /** Reference "today" (yyyy-MM-dd) for "Ends in N days"; defaults to the Dubai date. */
  today?: string;
}

const COPY = {
  en: {
    until: 'until',
    included: 'Included services',
    book: 'Book',
    visit: 'Visit garage',
    valid: 'Valid',
    endsIn: (n: number) => (n <= 0 ? 'Ends today' : n === 1 ? 'Ends in 1 day' : `Ends in ${n} days`),
    viewOffer: 'View offer',
    termsBase: 'Discount applies at checkout on the service price. Garagat promo codes still work on top.',
    left: (n: number) => `${n} bookings left on this offer.`,
    by: 'Offer by the garage.',
    phoneLabel: 'Offer preview on a phone',
    home: 'Home',
    search: 'Search for the service or the garage',
  },
  ar: {
    until: 'حتى',
    included: 'الخدمات المشمولة',
    book: 'احجز',
    visit: 'زيارة الكراج',
    valid: 'صالح',
    endsIn: (n: number) => (n <= 0 ? 'ينتهي اليوم' : n === 1 ? 'ينتهي خلال يوم واحد' : n === 2 ? 'ينتهي خلال يومين'
      : n <= 10 ? `ينتهي خلال ${n} أيام` : `ينتهي خلال ${n} يومًا`),
    viewOffer: 'عرض التفاصيل',
    termsBase: 'يُطبّق الخصم عند الدفع على سعر الخدمة. تبقى أكواد خصم كراجات صالحة فوقه.',
    left: (n: number) => (n >= 3 && n <= 10 ? `متبقٍ ${n} حجوزات على هذا العرض.` : `متبقٍ ${n} حجزًا على هذا العرض.`),
    by: 'العرض مقدّم من الكراج.',
    phoneLabel: 'معاينة العرض على الهاتف',
    home: 'الرئيسية',
    search: 'ابحث عن الخدمة أو الكراج',
  },
};

const fmtNum = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });
const aed = (n: number) => `AED ${fmtNum(n)}`;

function badge(type: 'PERCENT' | 'FIXED', value: number, lang: 'en' | 'ar'): string {
  const v = fmtNum(Number.isFinite(value) ? value : 0);
  if (type === 'PERCENT') return lang === 'ar' ? `خصم ${v}٪` : `${v}% OFF`;
  return lang === 'ar' ? `خصم ${v} درهم` : `AED ${v} OFF`;
}

function badgeUpTo(type: 'PERCENT' | 'FIXED', value: number, lang: 'en' | 'ar'): string {
  const v = fmtNum(Number.isFinite(value) ? value : 0);
  if (type === 'PERCENT') return lang === 'ar' ? `خصم حتى ${v}٪` : `Up to ${v}% OFF`;
  return lang === 'ar' ? `خصم حتى ${v} درهم` : `Up to AED ${v} OFF`;
}

function dubaiToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

function utcMs(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return Date.UTC(y, (m || 1) - 1, d || 1);
}

function shortDate(iso: string, lang: 'en' | 'ar'): string {
  return new Date(utcMs(iso)).toLocaleDateString(lang === 'ar' ? 'ar-AE' : 'en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

const PHONE_CSS = `
.ofp{--ofp-navy:#030c54;--ofp-yellow:#fff200;--ofp-bg:#f3f5f9;--ofp-card:#ffffff;--ofp-fg:#101633;--ofp-muted:#6c7190;--ofp-line:#e3e7ef;font-family:'Alexandria',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;width:300px;max-width:100%;aspect-ratio:300/640;border-radius:36px;background:#111;padding:8px;box-shadow:0 10px 30px rgba(0,0,0,.25);margin:0 auto;box-sizing:border-box}
.ofp *{box-sizing:border-box}
.ofp--dark{--ofp-bg:#0f1220;--ofp-card:#171b2e;--ofp-fg:#eceef7;--ofp-muted:#a3a8c2;--ofp-line:#2a2f45}
.ofp__screen{background:var(--ofp-bg);color:var(--ofp-fg);border-radius:29px;height:100%;overflow:hidden;position:relative;display:flex;flex-direction:column}
.ofp__island{position:absolute;top:9px;left:50%;transform:translateX(-50%);width:84px;height:24px;background:#000;border-radius:14px;z-index:3}
.ofp__status{height:42px;flex:none}
.ofp__home{padding:0 12px;display:flex;flex-direction:column;gap:8px}
.ofp__search{height:34px;border-radius:12px;background:var(--ofp-card);border:1px solid var(--ofp-line);display:flex;align-items:center;padding:0 10px;font-size:10px;color:var(--ofp-muted)}
.ofp__card{position:relative;width:100%;aspect-ratio:2/1;border-radius:16px;overflow:hidden;color:#fff;background:linear-gradient(135deg,#030c54,#1b2a8a 60%,#3a4cc0)}
.ofp__card img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block}
.ofp__scrim{position:absolute;inset:0;background:linear-gradient(90deg,rgba(3,12,84,.9),rgba(3,12,84,.2))}
.ofp[dir="rtl"] .ofp__scrim{background:linear-gradient(270deg,rgba(3,12,84,.9),rgba(3,12,84,.2))}
.ofp__copy{position:absolute;inset:10px 12px;display:flex;flex-direction:column;justify-content:space-between}
.ofp__tag{align-self:flex-start;font-size:9px;font-weight:700;background:var(--ofp-yellow);color:#101633;border-radius:5px;padding:2px 6px;letter-spacing:.03em}
.ofp__title{font-size:14px;font-weight:700;line-height:1.15;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}
.ofp__sub{font-size:9px;opacity:.95;margin-top:2px}
.ofp__sub b{color:var(--ofp-yellow)}
.ofp__who{display:flex;justify-content:space-between;align-items:flex-end;font-size:9px;gap:6px}
.ofp__cta{background:var(--ofp-yellow);color:#101633;font-weight:700;border-radius:999px;padding:3px 9px;white-space:nowrap}
.ofp__dots{display:flex;gap:4px;justify-content:center}
.ofp__dots i{width:5px;height:5px;border-radius:3px;background:#c9ccd9}
.ofp__dots i.on{width:16px;background:var(--ofp-navy)}
.ofp--dark .ofp__dots i.on{background:var(--ofp-yellow)}
.ofp__dim{position:absolute;inset:0;top:42%;background:rgba(0,0,0,.28)}
.ofp__sheet{position:absolute;left:0;right:0;bottom:0;top:46%;background:var(--ofp-card);border-radius:20px 20px 0 0;box-shadow:0 -6px 24px rgba(0,0,0,.2);padding:8px 12px 10px;display:flex;flex-direction:column;gap:6px;font-size:10px;overflow:hidden}
.ofp__grab{width:34px;height:4px;border-radius:2px;background:var(--ofp-line);margin:0 auto 2px}
.ofp__row{display:flex;justify-content:space-between;align-items:center;gap:6px}
.ofp__garage{font-weight:700;font-size:11px}
.ofp__muted{color:var(--ofp-muted);font-size:9px}
.ofp__eyebrow{font-size:8px;letter-spacing:.06em;text-transform:uppercase;color:var(--ofp-muted);font-weight:600}
.ofp__items{display:flex;flex-direction:column;gap:5px;overflow:hidden}
.ofp__svc{display:flex;justify-content:space-between;align-items:center;border:1px solid var(--ofp-line);border-radius:10px;padding:6px 8px;gap:6px}
.ofp__svc-name{font-weight:600;font-size:10px}
.ofp__price{font-size:9px;color:var(--ofp-muted)}
.ofp__price s{margin-inline-end:4px}
.ofp__price b{display:inline-block;background:var(--ofp-yellow);color:#101633;border-radius:5px;padding:0 5px;font-weight:700}
.ofp__btn{background:var(--ofp-navy);color:#fff;border-radius:8px;padding:4px 9px;font-size:9px;font-weight:600;white-space:nowrap}
.ofp--dark .ofp__btn{background:var(--ofp-yellow);color:#101633}
.ofp__btn--ghost{background:transparent;color:var(--ofp-navy);border:1px solid var(--ofp-navy)}
.ofp--dark .ofp__btn--ghost{background:transparent;color:var(--ofp-fg);border-color:var(--ofp-fg)}
.ofp__terms{font-size:8px;color:var(--ofp-muted);margin-top:auto}
.ofp__ltr{direction:ltr;unicode-bidi:isolate}
`;

function OfferPhonePreview(props: OfferPhonePreviewProps) {
  const {
    lang, theme, garageName, garageRating, garageRatingCount,
    titleEn, titleAr, subtitleEn, subtitleAr, discountType, discountValue, badgeUpTo: upTo,
    bannerUrl, bannerArUrl, startsAt, endsAt, items, maxBookings, bookingsUsed,
  } = props;
  const c = COPY[lang];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  // AR title falls back to EN; AR banner falls back to the EN image (requirements §4 RTL).
  const title = (lang === 'ar' && titleAr?.trim()) ? titleAr : (titleEn || '—');
  const subtitle = (lang === 'ar' && subtitleAr?.trim()) ? subtitleAr : subtitleEn;
  const image = (lang === 'ar' && bannerArUrl) ? bannerArUrl : bannerUrl;
  // Items arrive in the server's order (largest saving first), so the first is the app's lead item.
  const lead = items[0] ?? null;
  const today = props.today ?? dubaiToday();
  const daysLeft = endsAt ? Math.round((utcMs(endsAt) - utcMs(today)) / 86400000) : null;
  const left = maxBookings != null ? Math.max(maxBookings - (bookingsUsed ?? 0), 0) : null;
  const rating = garageRating != null
    ? <span className="ofp__ltr">★ {garageRating.toFixed(1)}{garageRatingCount != null ? ` (${garageRatingCount})` : ''}</span>
    : null;

  return (
    <div className={`ofp${theme === 'dark' ? ' ofp--dark' : ''}`} dir={dir} lang={lang} role="figure" aria-label={c.phoneLabel} data-testid="offer-phone-preview">
      <style>{PHONE_CSS}</style>
      <div className="ofp__screen">
        <div className="ofp__island" />
        <div className="ofp__status" />
        <div className="ofp__home">
          <div className="ofp__search">{c.search}</div>
          <div className="ofp__card">
            {image && <img src={image} alt="" />}
            <div className="ofp__scrim" />
            <div className="ofp__copy">
              <span className="ofp__tag" data-testid="offer-preview-badge">{upTo ? badgeUpTo(upTo.type, upTo.value, lang) : badge(discountType, discountValue, lang)}</span>
              <div>
                <div className="ofp__title">{title}</div>
                {lead ? (
                  <div className="ofp__sub" data-testid="offer-preview-price">
                    <span className="ofp__ltr">{aed(lead.listPrice)}</span> {lang === 'ar' ? '←' : '→'} <b className="ofp__ltr">{aed(lead.discountedPrice)}</b>
                    {endsAt ? <> · {c.until} {shortDate(endsAt, lang)}</> : null}
                  </div>
                ) : subtitle ? <div className="ofp__sub">{subtitle}</div> : null}
              </div>
              <div className="ofp__who">
                <span>{garageName}{rating ? <> · {rating}</> : null}</span>
                <span className="ofp__cta">{c.viewOffer}</span>
              </div>
            </div>
          </div>
          <div className="ofp__dots" aria-hidden="true"><i className="on" /><i /><i /></div>
        </div>
        <div className="ofp__dim" />
        <div className="ofp__sheet">
          <div className="ofp__grab" />
          <div className="ofp__row">
            <div>
              <div className="ofp__garage">{garageName}</div>
              {rating && <div className="ofp__muted">{rating}</div>}
            </div>
            <span className="ofp__btn ofp__btn--ghost">{c.visit}</span>
          </div>
          {subtitle && <div className="ofp__muted">{subtitle}</div>}
          {(startsAt || endsAt) && (
            <div className="ofp__row ofp__muted">
              <span>{c.valid} {startsAt ? shortDate(startsAt, lang) : ''} – {endsAt ? shortDate(endsAt, lang) : ''}</span>
              {daysLeft != null && <span>{c.endsIn(daysLeft)}</span>}
            </div>
          )}
          <div className="ofp__eyebrow">{c.included} ({items.length})</div>
          <div className="ofp__items">
            {items.slice(0, 3).map((it) => (
              <div className="ofp__svc" key={it.offeringId}>
                <div>
                  <div className="ofp__svc-name">{it.name}</div>
                  <div className="ofp__price"><s className="ofp__ltr">{aed(it.listPrice)}</s><b className="ofp__ltr">{aed(it.discountedPrice)}</b></div>
                </div>
                <span className="ofp__btn">{c.book}</span>
              </div>
            ))}
          </div>
          <div className="ofp__terms">
            {c.termsBase} {left != null ? c.left(left) + ' ' : ''}{c.by}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OfferPhonePreview;
